import React, { useEffect, useState } from "react"
import { get, post, put } from "../../lib/api"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Badge } from "../ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"

const OPEN_STATUS_OPTIONS = ["unread", "read", "in progress"]
const ALL_STATUS_OPTIONS = ["unread", "read", "in progress", "fixed", "archived"]

function normalizeFeedbackData(data) {
    if (Array.isArray(data) && data.length && typeof data[0] === 'object') return data
    if (data && typeof data === 'object' && data.user && Array.isArray(data.user)) {
        const length = data.user.length
        return Array.from({ length }, (_, i) => ({
            user: data.user[i],
            feedback: data.feedback ? data.feedback[i] : '',
            email: data.email ? data.email[i] : '',
            page: data.page ? data.page[i] : '',
            status: data.status ? data.status[i] : 'unread',
            id: data.id ? data.id[i] : i,
        }))
    }
    return []
}

const StatusBoxes = ({ currentStatus, onStatusChange, disabled }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'unread': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
            case 'read': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800'
            case 'in progress': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
            default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800'
        }
    }

    return (
        <div className="flex flex-col gap-1">
            {OPEN_STATUS_OPTIONS.map(status => (
                <button
                    key={status}
                    className={`px-2 py-1 text-xs font-medium rounded-md border-2 transition-all duration-200 w-24 h-6 ${
                        currentStatus === status
                            ? 'ring-2 ring-offset-1 ring-blue-500 shadow-md transform scale-105'
                            : 'hover:shadow-sm hover:scale-102'
                    } ${getStatusColor(status)} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:brightness-110'}`}
                    onClick={() => !disabled && onStatusChange(status)}
                    disabled={disabled}
                >
                    {status}
                </button>
            ))}
        </div>
    )
}

const FeedbackTable = ({ feedback, onResolve, resolvingId, onStatusChange, editableStatus, highlightResolved, statusOptions, title }) => {
    const rows = normalizeFeedbackData(feedback)
    return (
        <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
            <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    {title}
                    <Badge variant="secondary" className="ml-auto bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                        {rows.length} {rows.length === 1 ? 'item' : 'items'}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto rounded-xl">
                    <Table className="min-w-full">
                        <TableHeader className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                            <TableRow className="border-0">
                                <TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">User</TableHead>
                                <TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">Email</TableHead>
                                <TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">Page</TableHead>
                                <TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">Feedback</TableHead>
                                <TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.length > 0 ? rows.map((row, idx) => {
                                const isResolved = row.status === "fixed" || row.status === "archived" || row.status === "resolved"
                                const feedbackText = row.feedback || row.text || ""
                                const isLongFeedback = feedbackText.length > 100
                                
                                return (
                                    <TableRow
                                        key={row.id || idx}
                                        className={`border-b border-gray-100 dark:border-gray-700 transition-all duration-200 ${
                                            isResolved && highlightResolved 
                                                ? "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20" 
                                                : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                                        }`}
                                    >
                                        <TableCell className="py-4">
                                            <div className="font-medium text-gray-900 dark:text-gray-100">
                                                {row.name || row.user || "Anonymous"}
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <div className="text-gray-600 dark:text-gray-400">
                                                {row.email || "—"}
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <div className="text-gray-600 dark:text-gray-400 max-w-xs truncate">
                                                {row.pageUrl || row.page || "—"}
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="text-gray-700 dark:text-gray-300 max-w-md cursor-pointer">
                                                            {isLongFeedback ? (
                                                                <div className="line-clamp-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                                    {feedbackText.substring(0,150)}...
                                                                </div>
                                                            ) : (
                                                                <div className="line-clamp-2">
                                                                    {feedbackText}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TooltipTrigger>
                                                    {isLongFeedback && (
                                                        <TooltipContent className="max-w-md p-3">
                                                            <p className="text-sm whitespace-pre-wrap">{feedbackText}</p>
                                                        </TooltipContent>
                                                    )}
                                                </Tooltip>
                                            </TooltipProvider>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <div className="space-y-1.5">
                                                {editableStatus ? (
                                                    <StatusBoxes
                                                        currentStatus={row.status || "unread"}
                                                        onStatusChange={(newStatus) => onStatusChange(row, newStatus)}
                                                        disabled={false}
                                                    />
                                                ) : (
                                                    <Badge 
                                                        variant={row.status === "resolved" ? "default" : "secondary"}
                                                        className={row.status === "resolved" ? "bg-green-500 hover:bg-green-600" : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"}
                                                    >
                                                        {row.status || "unread"}
                                                    </Badge>
                                                )}
                                                {onResolve && (
                                                    <button
                                                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed text-xs w-24 h-6"
                                                        disabled={resolvingId === (row.id || idx)}
                                                        onClick={() => onResolve(row, row.id || idx)}
                                                    >
                                                        {resolvingId === (row.id || idx) ? "Resolving..." : "Resolve"}
                                                    </button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-12">
                                        <div className="text-gray-500 dark:text-gray-400">
                                            <div className="text-lg font-medium mb-2">No feedback available</div>
                                            <div className="text-sm">When feedback is submitted, it will appear here.</div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}

const FeedbackManager = () => {
    const [feedback, setFeedback] = useState([])
    const [loading, setLoading] = useState(true)
    const [resolvingId, setResolvingId] = useState(null)
    const [confirmDialog, setConfirmDialog] = useState({ open: false, row: null, id: null })

    useEffect(() => {
        const fetchFeedback = async () => {
            setLoading(true)
            try {
                const result = await get("/administration?method=getFeedback")
                setFeedback(result)
            } catch (e) {
                setFeedback([])
            }
            setLoading(false)
        }
        fetchFeedback()
    }, [])

    const handleStatusChange = async (row, newStatus) => {
        console.log("Updating status:", { id: row.id, oldStatus: row.status, newStatus })
        
        setFeedback(prev => {
            const rows = normalizeFeedbackData(prev)
            return rows.map(fb => (fb.id === row.id ? { ...fb, status: newStatus } : fb))
        })
        
        try {
            const requestData = {
                method: "updateFeedbackStatus",
                status: newStatus,
                user: row.user || row.name || "Anonymous",
                feedback: row.feedback || row.text || ""
            }
            console.log("Sending request:", requestData)
            
            const response = await put(requestData, "/administration")
            console.log("Response:", response)
            
            if (response.error) {
                toast.error("Failed to update status")
                console.error("Backend error:", response)
            } else {
                toast.success("Status updated successfully")
            }
        } catch (e) {
            toast.error("Failed to update status")
            console.error("Error updating status:", e)
        }
    }

    const handleResolveClick = (row, id) => {
        setConfirmDialog({ open: true, row, id })
    }

    const handleResolveConfirm = async () => {
        const { row, id } = confirmDialog
        setConfirmDialog({ open: false, row: null, id: null })
        setResolvingId(id)
        
        try {
            const requestData = {
                method: "resolveFeedback",
                user: row.user || row.name || "Anonymous",
                feedback: row.feedback || row.text || ""
            }
            console.log("Sending resolve request:", requestData)
            
            const response = await put(requestData, "/administration")
            console.log("Resolve response:", response)
            
            if (response.error) {
                toast.error("Failed to resolve feedback")
                console.error("Backend error:", response)
            } else {
                setFeedback(prev => {
                    const rows = normalizeFeedbackData(prev)
                    return rows.map(fb => (fb.id === (row.id ?? id) ? { ...fb, status: "resolved" } : fb))
                })
                toast.success("Feedback resolved successfully")
            }
        } catch (e) {
            toast.error("Failed to resolve feedback")
            console.error("Error resolving feedback:", e)
        }
        setResolvingId(null)
    }

    const allRows = normalizeFeedbackData(feedback)
    const openRows = allRows.filter(row => row.status !== "fixed" && row.status !== "archived" && row.status !== "resolved")
    const resolvedRows = allRows.filter(row => row.status === "fixed" || row.status === "archived" || row.status === "resolved")

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto py-8">
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <div className="text-gray-600 dark:text-gray-400">Loading feedback...</div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-7xl mx-auto py-8 space-y-8">
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Feedback Management</h1>
            </div>
            
            <div className="space-y-8">
                <FeedbackTable
                    feedback={openRows}
                    onResolve={handleResolveClick}
                    resolvingId={resolvingId}
                    onStatusChange={handleStatusChange}
                    editableStatus={true}
                    statusOptions={OPEN_STATUS_OPTIONS}
                    title="Active Feedback"
                />
                
                <FeedbackTable
                    feedback={resolvedRows}
                    editableStatus={false}
                    highlightResolved={true}
                    statusOptions={ALL_STATUS_OPTIONS}
                    title="Resolved Feedback"
                />
            </div>

            <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, row: null, id: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Resolution</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to resolve this feedback? This action will send an email notification to the user if they provided an email address.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDialog({ open: false, row: null, id: null })}>
                            Cancel
                        </Button>
                        <Button onClick={handleResolveConfirm} className="bg-green-600 hover:bg-green-700">
                            Yes, Resolve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default FeedbackManager